/**
 * GAMIFICATION of Agape (F4) — domain adapter over `gamification-core`.
 *
 * PURE PROJECTION of data that account ALREADY synchronizes (devotional
 * GAMIFICATION_CORE §gotchas): mis progresos devocionales
 * (`my_devotional_progress`), attendance check-ins, and group
 * memberships (`my_attendance_recognition`). Does not touch write-path or expose
 * third-party data: everything scored belongs to user.
 *
 * Double OPT-IN (by church and by account): UI only displays gaming
 * gamification if `gamificationVisible(...)`. Never penalizes — only adds.
 */

import { type DevotionalProgressLike, isDone } from "../devotional/progress";
import { countsAsPresent } from "../attendance";
import type { GameEvent, PlayerState } from "../gamification/types";
import { applyEvents, type GamificationConfig, initialState } from "../gamification/engine";
import { counterAtLeast, streakAtLeast } from "../gamification/badges";
import { dayKeyUTC } from "../gamification/streaks";

/** Domain events that award points. */
export const DEVOTIONAL_DONE = "devotional.done";
export const SERVICE_ATTENDED = "service.attended";
export const SERVICE_MEMBER = "service.member";

/** Devotional streak line. */
export const DEVOTIONAL_STREAK_LINE = "devotional";

export const AGAPE_GAMIFICATION_CONFIG: GamificationConfig = {
  xp: {
    [DEVOTIONAL_DONE]: 10,
    [SERVICE_ATTENDED]: 5,
    [SERVICE_MEMBER]: 15,
  },
  streakLines: { [DEVOTIONAL_DONE]: [DEVOTIONAL_STREAK_LINE] },
  badges: [
    {
      id: "devotional-first",
      title: "badge.devotional-first",
      when: counterAtLeast(DEVOTIONAL_DONE, 1),
    },
    {
      id: "devotional-streak-7",
      title: "badge.devotional-streak-7",
      when: streakAtLeast(DEVOTIONAL_STREAK_LINE, 7),
      bonusXp: 30,
    },
    {
      id: "devotional-streak-30",
      title: "badge.devotional-streak-30",
      when: streakAtLeast(DEVOTIONAL_STREAK_LINE, 30),
      bonusXp: 120,
    },
    {
      id: "service-member",
      title: "badge.service-member",
      when: counterAtLeast(SERVICE_MEMBER, 1),
    },
    {
      id: "service-faithful-10",
      title: "badge.service-faithful-10",
      when: counterAtLeast(SERVICE_ATTENDED, 10),
      bonusXp: 50,
    },
  ],
};

/** Structural `attendance_entry` row that awards points (my linked person). */
export interface RecognitionAttendanceLike {
  id: string;
  person_id: string | null;
  status: string | null;
  checked_in_at: string | null;
  created_at: string | null;
}

/** Structural row of `group_member` (recognition for serving in group). */
export interface GroupMemberLike {
  id: string;
  person_id: string | null;
  created_at: string | null;
}

export interface AgapeEventSource {
  /** MY devotional progress (sync only delivers own). */
  progress: readonly DevotionalProgressLike[];
  userId: string;
  /** Directory people linked to my account. */
  myPersonIds: readonly string[];
  attendance?: readonly RecognitionAttendanceLike[];
  memberships?: readonly GroupMemberLike[];
}

function ms(iso: string | null | undefined): number | null {
  if (!iso) {
    return null;
  }
  const t = Date.parse(iso.includes("T") ? iso : `${iso}T00:00:00Z`);
  return Number.isNaN(t) ? null : t;
}

/**
 * Derives `GameEvent`s from local state (deterministic and re-computable: no
 * incremental state to corrupt). Sorted by `at` (`toSorted`).
 */
export function buildAgapeEvents(source: AgapeEventSource): GameEvent[] {
  const mine = new Set(source.myPersonIds);
  const events: GameEvent[] = [];

  for (const p of source.progress) {
    if (p.user_id !== source.userId || !isDone(p) || !p.done_on) {
      continue;
    }
    const at = ms(p.done_on);
    if (at === null) {
      continue;
    }
    events.push({ type: DEVOTIONAL_DONE, at, day: p.done_on });
  }

  for (const entry of source.attendance ?? []) {
    if (!entry.person_id || !mine.has(entry.person_id) || !countsAsPresent(entry.status)) {
      continue;
    }
    const at = ms(entry.checked_in_at ?? entry.created_at);
    if (at === null) {
      continue;
    }
    events.push({ type: SERVICE_ATTENDED, at, day: dayKeyUTC(at) });
  }

  for (const member of source.memberships ?? []) {
    if (!member.person_id || !mine.has(member.person_id)) {
      continue;
    }
    const at = ms(member.created_at);
    if (at === null) {
      continue;
    }
    events.push({ type: SERVICE_MEMBER, at, day: dayKeyUTC(at) });
  }

  return events.toSorted((a, b) => a.at - b.at);
}

/** Player state recomputed from scratch over derived events. */
export function computeAgapePlayer(events: readonly GameEvent[]): PlayerState {
  return applyEvents(initialState(0), events, AGAPE_GAMIFICATION_CONFIG).state;
}

/**
 * Double opt-in: gaming layer only exists if CHURCH enabled it AND
 * ACCOUNT accepted participation. Any missing value = disabled.
 */
function isOn(v: number | boolean | null | undefined): boolean {
  return v === true || v === 1;
}

export function gamificationVisible(
  churchEnabled: number | boolean | null | undefined,
  userOptIn: number | boolean | null | undefined,
): boolean {
  return isOn(churchEnabled) && isOn(userOptIn);
}
